import React, { useEffect, useMemo, useState } from 'react';
import {
  BrainCircuit, CheckCircle2, Download, Eye, FileSearch, FlaskConical, History, RefreshCw,
  Route, ShieldCheck, Sparkles, Trash2, Upload, Wand2, Clock3
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { id: 'analyze', label: 'Analizar', icon: FileSearch },
  { id: 'humanize', label: 'Humanizar', icon: Wand2 },
  { id: 'sources', label: 'Fuentes', icon: ShieldCheck },
  { id: 'methodology', label: 'Metodología', icon: FlaskConical },
  { id: 'rewrite', label: 'Reescribir', icon: Sparkles },
  { id: 'final', label: 'Informe final', icon: BrainCircuit },
];

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-semibold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

const LayerCard = ({ item }) => (
  <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`tesix-layer-${item.id}`}>
    <p className="text-sm font-semibold text-text-heading">{item.name}</p>
    <p className="mt-2 text-sm text-text-body">{item.goal}</p>
  </div>
);

const ScoreBadge = ({ label, value, testId }) => (
  <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-xs text-primary" data-testid={testId}>
    <span className="text-text-muted">{label}</span>
    <span className="ml-2 font-semibold">{value}</span>
  </div>
);

const bytesToMbLabel = (value) => `${Number(value || 0).toFixed(2)} MB`;

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error && error.message && error.message !== '[object Object]') return error.message;
  if (typeof error === 'object') {
    if (typeof error.detail === 'string') return error.detail;
    if (typeof error.message === 'string' && error.message !== '[object Object]') return error.message;
    try {
      return JSON.stringify(error);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

export default function Neuralia369TesixHub({ authFetch }) {
  const requester = (url, options = {}) => authFetch(url, options);
  const isAdminContext = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  const [dashboard, setDashboard] = useState(null);
  const [documentsState, setDocumentsState] = useState(null);
  const [runningAction, setRunningAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [methodologyJob, setMethodologyJob] = useState(null);
  const [observationForm, setObservationForm] = useState({ title: '', description: '', chapter: '', priority: 'media', status: 'pendiente' });
  const [form, setForm] = useState({
    project_id: '',
    project_name: '',
    student_name: '',
    thesis_topic: '',
    objective: '',
    section_name: '',
    rewrite_goal: '',
    text: '',
  });
  const [uploadForm, setUploadForm] = useState({
    title: '',
    project_id: '',
    thesis_topic: '',
    student_name: '',
    notes: '',
    document_file: null,
  });

  const loadDashboard = async (showToast = false) => {
    try {
      setLoading(true);
      const [dashboardRes, docsRes] = await Promise.all([
        requester(isAdminContext ? `${API}/admin/tesis/dashboard` : `${API}/tesis/dashboard`),
        requester(`${API}/tesix/documents`),
      ]);
      const dashboardData = await dashboardRes.json();
      const docsData = await docsRes.json();
      if (!dashboardRes.ok) throw new Error(dashboardData.detail || 'No se pudo cargar Neuralia369Tesix');
      if (!docsRes.ok) throw new Error(docsData.detail || 'No se pudieron cargar los documentos');
      setDashboard(dashboardData);
      setDocumentsState(docsData);
      if (!isAdminContext) {
        const workflowRes = await requester(`${API}/tesix/workflow`);
        const workflowJson = await workflowRes.json();
        if (workflowRes.ok) setWorkflowData(workflowJson);
      }
      if (showToast) toast.success('Neuralia369Tesix actualizado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar Neuralia369Tesix'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(false);
  }, []);

  useEffect(() => {
    if (!methodologyJob?.id || methodologyJob?.status === 'completed' || methodologyJob?.status === 'failed') return undefined;
    const timer = setInterval(async () => {
      const res = await requester(`${API}/tesix/methodology/async/${methodologyJob.id}`);
      const data = await res.json();
      if (!res.ok) return;
      setMethodologyJob(data.job);
      if (data.job.status === 'completed' && data.job.result) {
        setResult(data.job.result);
        setForm((current) => ({ ...current, project_id: data.job.result.project_id || current.project_id }));
        toast.success('Metodología completada');
        await loadDashboard(false);
      }
      if (data.job.status === 'failed') {
        toast.error(data.job.error_message || 'La metodología asíncrona falló');
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [methodologyJob]);

  const runAction = async (actionId) => {
    if (!form.text && !form.project_id) {
      toast.error('Añade texto o selecciona un project_id previo');
      return;
    }
    try {
      setRunningAction(actionId);
      const payload = form.project_id
        ? { ...form, text: '' }
        : form;
      if (actionId === 'methodology') {
        const asyncRes = await requester(`${API}/tesix/methodology/async`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const asyncData = await asyncRes.json();
        if (!asyncRes.ok) throw new Error(asyncData.detail || 'No se pudo iniciar la metodología');
        setMethodologyJob(asyncData.job);
        toast.success('Metodología en proceso. Seguimos trabajando sin bloquear la pantalla.');
        return;
      }
      const res = await requester(`${API}/tesis/${actionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo ejecutar la acción');
      setResult(data);
      setForm((current) => ({
        ...current,
        project_id: data.project_id || current.project_id,
        project_name: data.project?.project_name || current.project_name,
        thesis_topic: data.project?.thesis_topic || current.thesis_topic,
      }));
      setUploadForm((current) => ({
        ...current,
        project_id: data.project_id || current.project_id,
        thesis_topic: data.project?.thesis_topic || current.thesis_topic,
        student_name: data.project?.student_name || current.student_name,
      }));
      toast.success(`${ACTIONS.find((item) => item.id === actionId)?.label || 'Acción'} completado`);
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo ejecutar la acción'));
    } finally {
      setRunningAction(null);
    }
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadForm.document_file) {
      toast.error('Selecciona un archivo');
      return;
    }
    try {
      setUploading(true);
      const body = new FormData();
      Object.entries(uploadForm).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') body.append(key, value);
      });
      const res = await requester(`${API}/tesix/documents`, { method: 'POST', body });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo subir el documento');
      const uploadedDoc = data.document;
      setUploadForm({ title: '', project_id: uploadForm.project_id, thesis_topic: uploadForm.thesis_topic, student_name: uploadForm.student_name, notes: '', document_file: null });
      setForm((current) => ({
        ...current,
        project_id: uploadedDoc?.project_id || current.project_id,
        project_name: uploadedDoc?.title || current.project_name,
        thesis_topic: uploadedDoc?.thesis_topic || current.thesis_topic,
        student_name: uploadedDoc?.student_name || current.student_name,
      }));
      toast.success('Documento subido correctamente');
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo subir el documento'));
    } finally {
      setUploading(false);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const res = await requester(`${API}/tesix/documents/${documentId}/download`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'No se pudo descargar');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename || 'documento';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo descargar'));
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('¿Eliminar este documento del workspace de tesis?')) return;
    try {
      const res = await requester(`${API}/tesix/documents/${documentId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo eliminar');
      toast.success('Documento eliminado');
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar'));
    }
  };

  const openDocumentPreview = async (doc) => {
    try {
      const previewUrl = `${API}/tesix/documents/${doc.id}/preview`;
      if (doc.document_kind === 'pdf' || doc.document_kind === 'image') {
        setPreviewDocument({
          id: doc.id,
          title: doc.title,
          kind: doc.document_kind,
          src: previewUrl,
          filename: doc.original_filename,
        });
        return;
      }

      const res = await requester(previewUrl);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'No se pudo abrir la vista previa');
      }
      const text = await res.text();
      setPreviewDocument({
        id: doc.id,
        title: doc.title,
        kind: doc.document_kind,
        text,
        filename: doc.original_filename,
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo abrir la vista previa'));
    }
  };

  const extractDocumentToWorkspace = async (doc) => {
    try {
      const res = await requester(`${API}/tesix/documents/${doc.id}/extract-to-workspace`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo extraer el documento');
      setForm((current) => ({
        ...current,
        project_id: data.workspace_draft?.project_id || current.project_id,
        project_name: data.workspace_draft?.project_name || current.project_name,
        student_name: data.workspace_draft?.student_name || current.student_name,
        thesis_topic: data.workspace_draft?.thesis_topic || current.thesis_topic,
        text: data.workspace_draft?.text || current.text,
      }));
      setUploadForm((current) => ({
        ...current,
        project_id: data.workspace_draft?.project_id || current.project_id,
        thesis_topic: data.workspace_draft?.thesis_topic || current.thesis_topic,
        student_name: data.workspace_draft?.student_name || current.student_name,
      }));
      setWorkflowData((current) => current ? ({ ...current, current_project: { ...(current.current_project || {}), id: data.workspace_draft?.project_id, project_name: data.workspace_draft?.project_name, thesis_topic: data.workspace_draft?.thesis_topic, student_name: data.workspace_draft?.student_name } }) : current);
      toast.success('Documento extraído y cargado al workspace');
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo extraer el documento'));
    }
  };

  const loadDocumentIntoWorkspace = (doc) => {
    setUploadForm((current) => ({
      ...current,
      project_id: doc.project_id || current.project_id,
      thesis_topic: doc.thesis_topic || current.thesis_topic,
      student_name: doc.student_name || current.student_name,
      title: doc.title || current.title,
    }));
    setForm((current) => ({
      ...current,
      project_id: doc.project_id || current.project_id,
      project_name: doc.title || current.project_name,
      thesis_topic: doc.thesis_topic || current.thesis_topic,
      student_name: doc.student_name || current.student_name,
    }));
    toast.success('Documento vinculado al workspace');
  };

  const activeScores = useMemo(() => result?.layer_scores || dashboard?.recent_projects?.[0]?.layer_scores || {}, [result, dashboard]);

  const approvePhase = async (phaseId) => {
    try {
      const res = await requester(`${API}/tesix/workflow/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase_id: phaseId, approved: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo aprobar la fase');
      setWorkflowData((current) => current ? ({ ...current, workflow: data.workflow }) : current);
      toast.success('Fase aprobada');
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo aprobar la fase'));
    }
  };

  const runWordLab = async () => {
    if (!form.text) {
      toast.error('Carga texto en el workspace para usar WordLab');
      return;
    }
    try {
      setRunningAction('wordlab');
      const res = await requester(`${API}/tesix/wordlab/rewrite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: form.project_id,
          project_name: form.project_name,
          thesis_topic: form.thesis_topic,
          student_name: form.student_name,
          section_name: form.section_name,
          rewrite_goal: form.rewrite_goal,
          text: form.text,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo ejecutar WordLab');
      setResult({ ...data, output_text: data.revised_text, stage: 'wordlab' });
      setForm((current) => ({ ...current, text: data.revised_text, project_id: data.project_id || current.project_id }));
      toast.success('WordLab terminó la revisión de la sección');
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo ejecutar WordLab'));
    } finally {
      setRunningAction(null);
    }
  };

  const createObservation = async () => {
    if (!observationForm.title || !observationForm.description) {
      toast.error('Añade título y descripción de la observación');
      return;
    }
    try {
      const res = await requester(`${API}/tesix/observations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...observationForm,
          project_id: form.project_id || workflowData?.current_project?.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear la observación');
      setObservationForm({ title: '', description: '', chapter: '', priority: 'media', status: 'pendiente' });
      toast.success('Observación creada');
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo crear la observación'));
    }
  };

  const moveObservation = async (id, status) => {
    try {
      const res = await requester(`${API}/tesix/observations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo mover la observación');
      toast.success('Observación actualizada');
      await loadDashboard(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo mover la observación'));
    }
  };

  if (!dashboard || !documentsState) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="tesix-loading">Cargando Neuralia369Tesix...</div>;
  }

  return (
    <div className="space-y-6" data-testid="tesix-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="tesix-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.06fr_0.94fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Neuralia369Tesix</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">LLOPS académico · subida segura · historial · descarga privada</p>
            <h3 className="mt-4 max-w-4xl text-3xl font-semibold text-text-heading">Zona de tesis con trabajo por proyecto, documentos privados y trazabilidad operativa.</h3>
            <p className="mt-4 max-w-3xl text-sm text-text-body">Sube PDF, DOCX, TXT, JPG o PNG, gestiona descargas y ejecuta análisis, metodología, fuentes, reescritura e informe final desde un mismo hub.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="tesix-hero-chips">
              <span className="insight-chip">PDF/DOCX/TXT</span>
              <span className="insight-chip">JPG/PNG</span>
              <span className="insight-chip">Historial</span>
              <span className="insight-chip">Privado con login</span>
            </div>
            <a href="/neuralia369tesix" className="mt-5 inline-flex text-sm text-primary" data-testid="tesix-open-standalone-link">Abrir zona directa</a>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard label="Proyectos" value={dashboard.metrics.projects} helper="espacios de tesis activos" testId="tesix-metric-projects" />
            <MetricCard label="Documentos" value={documentsState.metrics.total_documents} helper="archivos privados en la zona" testId="tesix-metric-documents" />
            <MetricCard label="AI risk medio" value={dashboard.metrics.avg_ai_risk} helper="score activo del workspace" testId="tesix-metric-ai-risk" />
            <MetricCard label="Tamaño" value={bytesToMbLabel(documentsState.metrics.total_size_mb)} helper="ocupación actual" testId="tesix-metric-storage" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={() => loadDashboard(true)} className="btn-secondary" disabled={loading} data-testid="tesix-refresh-button">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </button>
      </div>

      {!isAdminContext && workflowData && (
        <div className="glass-card p-6" data-testid="tesix-student-workflow-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="editorial-kicker">Flujo de Trabajo — Neuralia369 Tesix</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Centro operativo para alumnos, clientes y partner de Perú</h4>
              <p className="mt-2 text-sm text-text-body">Desde aquí podéis subir avance, aprobar fases, abrir WordLab y trabajar estructura, metodología, fuentes y revisión.</p>
            </div>
            {methodologyJob && (
              <div className="rounded-[18px] border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary" data-testid="tesix-methodology-job-status">
                Metodología asíncrona: {methodologyJob.status}
              </div>
            )}
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="tesix-workflow-grid">
            {(workflowData.workflow || []).map((phase) => (
              <div key={phase.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`tesix-workflow-phase-${phase.id}`}>
                <div className="flex items-center gap-2 text-text-heading">
                  {phase.approved ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : phase.status === 'active' ? <Clock3 className="w-4 h-4 text-primary" /> : <Route className="w-4 h-4 text-text-muted" />}
                  <p className="font-semibold text-sm">{phase.title}</p>
                </div>
                <p className="mt-2 text-sm text-text-body">{phase.helper}</p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.16em] text-text-muted">{phase.status}</p>
                {!phase.approved && (
                  <button onClick={() => approvePhase(phase.id)} className="mt-3 btn-secondary !px-3 !py-2" data-testid={`tesix-approve-phase-${phase.id}`}>Aprobar fase</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!isAdminContext && workflowData && (
        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="glass-card p-6" data-testid="tesix-observation-form-card">
            <div className="flex items-center gap-2 text-text-heading"><History className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Observaciones por universidad</h4></div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <input className="input-kinesthetic md:col-span-2" placeholder="Título de la observación" value={observationForm.title} onChange={(e) => setObservationForm((current) => ({ ...current, title: e.target.value }))} data-testid="tesix-observation-title-input" />
              <input className="input-kinesthetic" placeholder="Capítulo o sección" value={observationForm.chapter} onChange={(e) => setObservationForm((current) => ({ ...current, chapter: e.target.value }))} data-testid="tesix-observation-chapter-input" />
              <select className="input-kinesthetic" value={observationForm.priority} onChange={(e) => setObservationForm((current) => ({ ...current, priority: e.target.value }))} data-testid="tesix-observation-priority-select">
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
              <textarea className="input-kinesthetic min-h-[160px] resize-y md:col-span-2" placeholder="Describe la observación y qué exige la universidad" value={observationForm.description} onChange={(e) => setObservationForm((current) => ({ ...current, description: e.target.value }))} data-testid="tesix-observation-description-input" />
            </div>
            <button onClick={createObservation} className="mt-4 btn-secondary" data-testid="tesix-observation-create-button">Crear observación</button>
          </div>

          <div className="glass-card p-6" data-testid="tesix-observation-kanban-card">
            <div className="flex items-center gap-2 text-text-heading"><Route className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Kanban de observaciones</h4></div>
            <div className="mt-4 grid gap-4 xl:grid-cols-5">
              {(workflowData.observations?.kanban || []).map((column) => (
                <div key={column.id} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`tesix-observation-column-${column.id}`}>
                  <p className="text-sm font-semibold text-text-heading">{column.label}</p>
                  <div className="mt-3 space-y-3">
                    {(column.items || []).map((item) => (
                      <div key={item.id} className="rounded-[16px] border border-border-subtle bg-card/40 p-3" data-testid={`tesix-observation-card-${item.id}`}>
                        <p className="text-sm font-medium text-text-heading">{item.title}</p>
                        <p className="mt-1 text-xs text-text-muted">{item.chapter || 'Sin capítulo'} · {item.priority}</p>
                        <p className="mt-2 text-sm text-text-body line-clamp-4">{item.description}</p>
                        <select className="input-kinesthetic mt-3" value={item.status} onChange={(e) => moveObservation(item.id, e.target.value)} data-testid={`tesix-observation-move-${item.id}`}>
                          <option value="pendiente">Pendiente</option>
                          <option value="en_revision">En revisión</option>
                          <option value="reescritura">Reescritura</option>
                          <option value="validado">Validado</option>
                          <option value="listo_entrega">Listo para entrega</option>
                        </select>
                      </div>
                    ))}
                    {!column.items?.length && <p className="text-xs text-text-muted">Sin tarjetas</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[0.98fr_1.02fr]">
        <div className="glass-card p-6" data-testid="tesix-upload-card">
          <div className="flex items-center gap-2 text-text-heading"><Upload className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Subir documentos</h4></div>
          <form className="mt-4 space-y-4" onSubmit={uploadDocument} data-testid="tesix-upload-form">
            <div className="grid gap-4 md:grid-cols-2">
              <input className="input-kinesthetic" placeholder="Título del documento" value={uploadForm.title} onChange={(e) => setUploadForm((current) => ({ ...current, title: e.target.value }))} data-testid="tesix-upload-title-input" />
              <input className="input-kinesthetic" placeholder="project_id (opcional)" value={uploadForm.project_id} onChange={(e) => setUploadForm((current) => ({ ...current, project_id: e.target.value }))} data-testid="tesix-upload-project-id-input" />
              <input className="input-kinesthetic" placeholder="Tema de tesis" value={uploadForm.thesis_topic} onChange={(e) => setUploadForm((current) => ({ ...current, thesis_topic: e.target.value }))} data-testid="tesix-upload-topic-input" />
              <input className="input-kinesthetic" placeholder="Alumno/a" value={uploadForm.student_name} onChange={(e) => setUploadForm((current) => ({ ...current, student_name: e.target.value }))} data-testid="tesix-upload-student-input" />
              <textarea className="input-kinesthetic min-h-[110px] resize-y md:col-span-2" placeholder="Notas internas del documento" value={uploadForm.notes} onChange={(e) => setUploadForm((current) => ({ ...current, notes: e.target.value }))} data-testid="tesix-upload-notes-input" />
              <input className="input-kinesthetic md:col-span-2" type="file" accept=".pdf,.docx,.txt,.jpg,.jpeg,.png" onChange={(e) => setUploadForm((current) => ({ ...current, document_file: e.target.files?.[0] || null }))} data-testid="tesix-upload-file-input" />
            </div>
            <button type="submit" className="btn-secondary" disabled={uploading} data-testid="tesix-upload-submit-button">{uploading ? 'Subiendo...' : 'Subir documento'}</button>
          </form>
        </div>

        <div className="glass-card p-6" data-testid="tesix-workspace-card">
          <div className="flex items-center gap-2 text-text-heading"><Route className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Workspace académico</h4></div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <input className="input-kinesthetic" placeholder="project_id existente (opcional)" value={form.project_id} onChange={(e) => setForm((current) => ({ ...current, project_id: e.target.value }))} data-testid="tesix-project-id-input" />
            <input className="input-kinesthetic" placeholder="Nombre del proyecto" value={form.project_name} onChange={(e) => setForm((current) => ({ ...current, project_name: e.target.value }))} data-testid="tesix-project-name-input" />
            <input className="input-kinesthetic" placeholder="Estudiante (opcional)" value={form.student_name} onChange={(e) => setForm((current) => ({ ...current, student_name: e.target.value }))} data-testid="tesix-student-name-input" />
            <input className="input-kinesthetic" placeholder="Tema de tesis" value={form.thesis_topic} onChange={(e) => setForm((current) => ({ ...current, thesis_topic: e.target.value }))} data-testid="tesix-topic-input" />
            <input className="input-kinesthetic md:col-span-2" placeholder="Objetivo principal" value={form.objective} onChange={(e) => setForm((current) => ({ ...current, objective: e.target.value }))} data-testid="tesix-objective-input" />
            <input className="input-kinesthetic" placeholder="Sección" value={form.section_name} onChange={(e) => setForm((current) => ({ ...current, section_name: e.target.value }))} data-testid="tesix-section-input" />
            <input className="input-kinesthetic" placeholder="Objetivo de reescritura" value={form.rewrite_goal} onChange={(e) => setForm((current) => ({ ...current, rewrite_goal: e.target.value }))} data-testid="tesix-rewrite-goal-input" />
            <textarea className="input-kinesthetic min-h-[220px] resize-y md:col-span-2" placeholder="Pega aquí el borrador o una sección de la tesis..." value={form.text} onChange={(e) => setForm((current) => ({ ...current, text: e.target.value }))} data-testid="tesix-text-input" />
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="tesix-action-grid">
            {ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button key={action.id} onClick={() => runAction(action.id)} className="rounded-[18px] border border-border-subtle bg-background/55 px-4 py-3 text-left transition hover:border-primary/40" disabled={runningAction === action.id} data-testid={`tesix-action-${action.id}`}>
                  <div className="flex items-center gap-2 text-text-heading"><Icon className="w-4 h-4 text-primary" /> <span className="font-medium">{runningAction === action.id ? 'Procesando...' : action.label}</span></div>
                </button>
              );
            })}
            <button onClick={runWordLab} className="rounded-[18px] border border-border-subtle bg-background/55 px-4 py-3 text-left transition hover:border-primary/40" disabled={runningAction === 'wordlab'} data-testid="tesix-action-wordlab">
              <div className="flex items-center gap-2 text-text-heading"><Wand2 className="w-4 h-4 text-primary" /> <span className="font-medium">{runningAction === 'wordlab' ? 'Procesando...' : 'WordLab'}</span></div>
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.04fr_0.96fr]">
        <div className="glass-card p-6" data-testid="tesix-documents-card">
          <div className="flex items-center gap-2 text-text-heading"><FileSearch className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Documentos subidos</h4></div>
          <div className="mt-4 space-y-3">
            {(documentsState.documents || []).map((doc) => (
              <div key={doc.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`tesix-document-${doc.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{doc.title}</p>
                    <p className="mt-1 text-xs text-text-muted">{doc.original_filename} · {doc.document_kind} · {doc.status}</p>
                    <p className="mt-2 text-sm text-text-body">{doc.thesis_topic || 'Sin tema'}{doc.student_name ? ` · ${doc.student_name}` : ''}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => loadDocumentIntoWorkspace(doc)} className="btn-secondary !px-3 !py-2" data-testid={`tesix-document-load-${doc.id}`}>Cargar</button>
                    <button onClick={() => extractDocumentToWorkspace(doc)} className="btn-secondary !px-3 !py-2" data-testid={`tesix-document-extract-${doc.id}`}>Extraer</button>
                    <button onClick={() => openDocumentPreview(doc)} className="btn-secondary !px-3 !py-2" data-testid={`tesix-document-preview-${doc.id}`}><Eye className="w-4 h-4" /></button>
                    <button onClick={() => downloadDocument(doc.id, doc.original_filename)} className="btn-secondary !px-3 !py-2" data-testid={`tesix-document-download-${doc.id}`}><Download className="w-4 h-4" /></button>
                    <button onClick={() => deleteDocument(doc.id)} className="btn-secondary !px-3 !py-2" data-testid={`tesix-document-delete-${doc.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
            {!documentsState.documents?.length && <p className="text-sm text-text-muted" data-testid="tesix-documents-empty">Todavía no hay documentos en Neuralia369Tesix.</p>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-card p-6" data-testid="tesix-preview-card">
            <div className="flex items-center gap-2 text-text-heading"><Eye className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Vista previa</h4></div>
            {!previewDocument && <p className="mt-4 text-sm text-text-muted" data-testid="tesix-preview-empty">Abre un documento para verlo aquí antes de extraerlo o descargarlo.</p>}
            {!!previewDocument && (
              <div className="mt-4 space-y-4" data-testid="tesix-preview-content">
                <div>
                  <p className="text-sm font-semibold text-text-heading">{previewDocument.title}</p>
                  <p className="mt-1 text-xs text-text-muted">{previewDocument.filename} · {previewDocument.kind}</p>
                </div>
                {previewDocument.kind === 'pdf' && (
                  <iframe src={previewDocument.src} title={previewDocument.title} className="h-[420px] w-full rounded-[20px] border border-border-subtle bg-background/55" data-testid="tesix-preview-pdf-frame" />
                )}
                {previewDocument.kind === 'image' && (
                  <img src={previewDocument.src} alt={previewDocument.title} className="max-h-[420px] w-full rounded-[20px] border border-border-subtle object-contain bg-background/55" data-testid="tesix-preview-image" />
                )}
                {(previewDocument.kind === 'txt' || previewDocument.kind === 'docx') && (
                  <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-[20px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid="tesix-preview-text">{previewDocument.text}</pre>
                )}
              </div>
            )}
          </div>

          <div className="glass-card p-6" data-testid="tesix-scorecard-card">
            <div className="flex items-center gap-2 text-text-heading"><BrainCircuit className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Scorecard LLOPS</h4></div>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="tesix-score-badges">
              <ScoreBadge label="AI risk" value={activeScores.ai_risk_score ?? 0} testId="tesix-score-ai-risk" />
              <ScoreBadge label="Humanización" value={activeScores.humanization_score ?? 0} testId="tesix-score-humanization" />
              <ScoreBadge label="Trazabilidad" value={activeScores.traceability_score ?? 0} testId="tesix-score-traceability" />
              <ScoreBadge label="Reverse-detector" value={activeScores.reverse_detector_score ?? 0} testId="tesix-score-detector" />
              <ScoreBadge label="Estructura" value={activeScores.structure_score ?? 0} testId="tesix-score-structure" />
              <ScoreBadge label="Metodología" value={activeScores.methodology_score ?? 0} testId="tesix-score-methodology" />
            </div>
          </div>

          <div className="glass-card p-6" data-testid="tesix-result-card">
            <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Resultado activo</h4></div>
            {result ? (
              <div className="mt-4 space-y-4">
                <p className="text-xs uppercase tracking-[0.18em] text-text-muted">{result.stage} · project_id {result.project_id}</p>
                {result.diagnosis && <p className="text-sm text-text-body" data-testid="tesix-result-diagnosis">{result.diagnosis}</p>}
                {result.output_text && <pre className="whitespace-pre-wrap rounded-[20px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid="tesix-result-output">{result.output_text}</pre>}
              </div>
            ) : (
              <p className="mt-4 text-sm text-text-muted" data-testid="tesix-result-empty">Sube documentos o ejecuta una acción para ver resultados.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="glass-card p-6" data-testid="tesix-layers-card">
          <div className="flex items-center gap-2 text-text-heading"><ShieldCheck className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Capas del sistema</h4></div>
          <div className="mt-4 space-y-3">
            {(dashboard.llops_layers || []).map((item) => <LayerCard key={item.id} item={item} />)}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="tesix-history-card">
          <div className="flex items-center gap-2 text-text-heading"><History className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Historial y trazabilidad</h4></div>
          <div className="mt-4 space-y-3">
            {(documentsState.history || []).slice(0, 12).map((item) => (
              <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`tesix-history-${item.id}`}>
                <p className="text-sm font-semibold text-text-heading">{item.title || 'Documento de tesis'}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-text-muted">{item.action}</p>
                <p className="mt-2 text-sm text-text-body">{item.actor_email}</p>
              </div>
            ))}
            {!documentsState.history?.length && <p className="text-sm text-text-muted" data-testid="tesix-history-empty">El historial aparecerá aquí cuando empieces a subir, descargar o borrar documentos.</p>}
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="tesix-projects-card">
        <div className="flex items-center gap-2 text-text-heading"><Route className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Proyectos recientes</h4></div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {(dashboard.recent_projects || []).map((item) => (
            <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`tesix-project-${item.id}`}>
              <p className="text-sm font-semibold text-text-heading">{item.project_name}</p>
              <p className="mt-1 text-xs text-text-muted">{item.thesis_topic} · {item.latest_stage}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <ScoreBadge label="AI risk" value={item.layer_scores?.ai_risk_score ?? 0} testId={`tesix-project-risk-${item.id}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}